import { transformAbbreviationCountry2FullCountry, uuid13 } from "@utils/util";
import report from "@report";
import { useAppDispatch, useAppSelector } from "@hooks/redux";
import {
  fetchProductInfo,
  fetchVideoDetail,
  fetchScriptGenerationResult,
  fetchScriptGenerationTask,
  fetchScriptGenerationResultTranslate,
  updateError,
  updateStatus,
  updateTranslateStatus,
  fetchSellingPointsResult,
  fetchSellingPointsTask,
  removeSellingPoints,
} from "./scriptRewriteSlice";
import aiStudioService from "@apis/aiStudioService";
import { useQueryParams } from "@hooks/useQueryParams";
import { fetchAiCredits } from "@store/globalSlice";
import {
  EMPTY,
  Subject,
  Subscription,
  defer,
  expand,
  filter,
  from,
  map,
  race,
  switchMap,
  take,
  takeUntil,
  timer,
} from "rxjs";

// --- Constants ---

const POLL_INTERVAL_MS = 5_000;
const LOOP_TIMEOUT_MS = 5 * 60 * 1_000;

const TERMINAL_CODES = new Set([2, 3, 4, 5, 6, 7]);

// 0: 开始任务, 1: 任务执行中, 2: 任务完成, 3: 任务超时, 4: 积分不足, 5: 参数不合法, 6: 资源没准备好, 7: 不支持
const FAILURE_REASONS: Record<number, { type: string; reason: string }> = {
  3: { type: "script_optimizer", reason: "task timeout" },
  4: { type: "script_optimizer", reason: "not enough credits" },
  5: { type: "script_export", reason: "Invalid parameter" },
  6: { type: "script_export", reason: "resource not ready" },
  7: { type: "script_export", reason: "not support" },
};

function reportResult(kaloType: string, value: "success" | "failed", reason?: string) {
  report.event("video_script_result", {
    kalo_type: kaloType,
    kalo_value: value,
    ...(reason ? { kalo_json: { reason } } : {}),
  });
}

// --- Polling state (module-level to survive re-renders) ---

let scriptGenSubscription: Subscription | undefined;
let scriptGenStop$ = new Subject<void>();
let sellingPointsTimer: ReturnType<typeof setInterval> | undefined;
let cancelled = false;

function clearScriptGenTimers() {
  scriptGenStop$.next();
  scriptGenSubscription?.unsubscribe();
  scriptGenSubscription = undefined;
}

// --- Hook ---

export const useVideoRewrite = () => {
  const [originalVideoID, originalVideoSource, originalCountry]: any = useQueryParams(["id", "videoSource", "country"]);
  const {
    country: _country,
    videoId,
    videoSource,
    productItem,
    selling_points,
    target_age,
    target_gender,
    imitation_level,
    language,
    rewriteScriptList,
  } = useAppSelector((state) => state.scriptRewriteReducer);
  const dispatch = useAppDispatch();

  const showTranslate = async (translate: boolean, activeScript: number) => {
    if (translate) {
      await dispatch(
        fetchScriptGenerationResultTranslate({
          activeScript: (activeScript % 2) + 1,
          scriptIndex: activeScript,
          requestId: rewriteScriptList[activeScript].requestId,
        }),
      );
    } else {
      dispatch(updateTranslateStatus({ activeScript, translate: false }));
    }
  };

  const requestVideoDetail = async () => {
    await dispatch(
      fetchVideoDetail({
        id: originalVideoID,
        country: originalCountry,
        videoSource: originalVideoSource,
      }),
    );
  };

  const requestProductInfo = async () => {
    const result: any = await dispatch(
      fetchProductInfo({
        videoId: originalVideoID,
        country: originalCountry,
        videoSource: originalVideoSource,
      }),
    );
    const productId = result.payload?.id;
    if (productId) {
      requestSellingPointers(productId, originalCountry);
    } else {
      dispatch(removeSellingPoints());
    }
  };

  const requestSellingPointers = (productId: string, country: string) => {
    if (!productId) {
      dispatch(removeSellingPoints());
      return;
    }

    const clearTimer = () => {
      clearInterval(sellingPointsTimer);
      sellingPointsTimer = undefined;
    };

    const tick = async () => {
      if (cancelled) {
        clearTimer();
        return;
      }

      try {
        const code = await dispatch(fetchSellingPointsTask({ id: productId, country })).unwrap();

        if (cancelled) {
          clearTimer();
          return;
        }

        if (code === 2) {
          clearTimer();
          await dispatch(fetchSellingPointsResult({ id: productId, attributeKey: "selling_points", country }));
        }
      } catch {
        clearTimer();
      }
    };

    tick();
    sellingPointsTimer = setInterval(tick, POLL_INTERVAL_MS);
  };

  const handleScriptGenSuccess = async (params: Record<string, any>, requestId: string) => {
    try {
      await dispatch(fetchScriptGenerationResult({ id: params.videoId, requestId, userInput: params }));
      reportResult("script_optimizer", "success");
    } catch (err) {
      dispatch(updateError(err));
      reportResult("script_optimizer", "failed", (err as any)?.message || String(err));
    } finally {
      dispatch(fetchAiCredits());
    }
  };

  const loopAITask = () => {
    const { id: productId, productTitle, priCateName, secCateName, terCateName, country } = productItem.data || {};
    if (!videoId && !productId) return Promise.resolve(false);

    cancelled = false;

    const params = {
      videoId,
      videoSource,
      product_id: productId || "",
      product_title: productTitle || "",
      category: [priCateName, secCateName, terCateName].filter(Boolean).join(" > ") || "",
      selling_points,
      target_age,
      target_gender,
      imitation_level,
      language,
      country,
    };

    report.event("video_script_generate", { kalo_type: "script_optimizer", kalo_json: params });

    const requestId = uuid13();
    const requestPayload = {
      id: params.videoId,
      requestId,
      userInput: {
        ...params,
        country: transformAbbreviationCountry2FullCountry(country),
      },
      videoSource: params.videoSource,
    };

    clearScriptGenTimers();
    scriptGenStop$.complete();
    scriptGenStop$ = new Subject<void>();

    const requestStatus$ = defer(() => from(dispatch(fetchScriptGenerationTask(requestPayload)).unwrap()));
    const terminalResult$ = requestStatus$.pipe(
      expand(({ code }) => {
        if (cancelled || code === null || code === undefined || TERMINAL_CODES.has(code)) {
          return EMPTY;
        }
        return timer(POLL_INTERVAL_MS).pipe(
          takeUntil(scriptGenStop$),
          switchMap(() => requestStatus$),
        );
      }),
      filter(({ code }) => code !== null && code !== undefined && TERMINAL_CODES.has(code)),
      take(1),
      map((result) => ({ type: "result" as const, result })),
      takeUntil(scriptGenStop$),
    );
    const timeout$ = timer(LOOP_TIMEOUT_MS).pipe(
      map(() => ({ type: "timeout" as const })),
      takeUntil(scriptGenStop$),
    );

    return new Promise((resolve) => {
      let settled = false;
      const settle = (value: boolean) => {
        if (settled) return;
        settled = true;
        resolve(value);
      };

      scriptGenSubscription = race(terminalResult$, timeout$)
        .pipe(take(1))
        .subscribe({
          next: (event) => {
            clearScriptGenTimers();
            if (event.type === "timeout") {
              dispatch(updateStatus("failed"));
              reportResult("script_optimizer", "failed", "client timeout");
              settle(true);
              return;
            }

            const { code } = event.result;
            if (code === 2) {
              handleScriptGenSuccess(params, requestId);
            } else {
              const meta = typeof code === "number" ? FAILURE_REASONS[code] : undefined;
              if (meta) reportResult(meta.type, "failed", meta.reason);
            }
            settle(true);
          },
          error: () => {
            clearScriptGenTimers();
            settle(false);
          },
          complete: () => {
            if (!settled) {
              settle(false);
            }
          },
        });
    });
  };

  const reset = () => {
    requestVideoDetail();
    requestProductInfo();
  };

  const init = async () => {
    if (originalVideoID && originalVideoSource) {
      requestVideoDetail();
      requestProductInfo();
    }
  };

  const fetchExampleVideo = async () => {
    const result = await aiStudioService.fetchExampleVideo<{ code: number }>();
    if (result.code === 2) {
      return (result as any).data;
    }
    return null;
  };

  const stopPolling = () => {
    cancelled = true;
    clearScriptGenTimers();
    clearInterval(sellingPointsTimer);
    sellingPointsTimer = undefined;
  };

  const resetCancelled = () => {
    cancelled = false;
  };

  return {
    init,
    loopAITask,
    reset,
    showTranslate,
    fetchExampleVideo,
    stopPolling,
    resetCancelled,
    requestSellingPointers,
  };
};
